import { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Gift, Flame } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface Props {
  playerId: string;
  onClaimed: () => void;
}

const REWARDS = [5, 10, 15, 20, 25, 30, 35, 40, 45, 50];

function todayUtc(): string {
  return new Date().toISOString().slice(0, 10);
}

export function DailyClaimCard({ playerId, onClaimed }: Props) {
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState(false);
  const [currentDay, setCurrentDay] = useState(0); // 0 = never claimed
  const [lastClaimDate, setLastClaimDate] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    const { data } = await supabase
      .from("game_daily_claims")
      .select("current_day,last_claim_date")
      .eq("player_id", playerId)
      .maybeSingle();
    if (data) {
      setCurrentDay(data.current_day);
      setLastClaimDate(data.last_claim_date);
    } else {
      setCurrentDay(0);
      setLastClaimDate(null);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (playerId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playerId]);

  const today = todayUtc();
  const alreadyClaimedToday = lastClaimDate === today;

  // Determine the day the user WOULD claim next
  let nextDay = 1;
  if (lastClaimDate) {
    const diffDays = Math.floor(
      (Date.parse(today) - Date.parse(lastClaimDate)) / 86_400_000
    );
    if (diffDays === 0) nextDay = currentDay; // already claimed
    else if (diffDays === 1) nextDay = Math.min(currentDay + 1, 10);
    else nextDay = 1; // streak broken
  }
  const nextReward = REWARDS[Math.max(0, nextDay - 1)];
  const streakBroken =
    lastClaimDate !== null &&
    !alreadyClaimedToday &&
    nextDay === 1 &&
    currentDay > 1;

  const handleClaim = async () => {
    setClaiming(true);
    try {
      const { data, error } = await supabase.rpc("claim_game_daily_reward");
      if (error) throw error;
      const res = data as { success: boolean; reason?: string; day?: number; reward?: number };
      if (!res?.success) {
        if (res?.reason === "already_claimed_today") {
          toast.info("You already claimed today. Come back tomorrow!");
        } else {
          toast.error(res?.reason || "Claim failed");
        }
        await load();
        return;
      }
      toast.success(`Day ${res.day} claimed! +$${res.reward}`);
      await load();
      onClaimed();
    } catch (e: any) {
      toast.error(e?.message || "Claim failed");
    } finally {
      setClaiming(false);
    }
  };

  return (
    <Card className="card-game-neon">
      <CardContent className="py-5 px-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-amber-400 via-pink-500 to-purple-500 flex items-center justify-center shadow-lg shadow-pink-500/40">
              <Gift className="w-5 h-5 text-white" />
            </div>
            <div>
              <p
                className="font-bold text-white text-sm"
                style={{ textShadow: "0 0 8px rgba(236, 72, 153, 0.5)" }}
              >
                Daily Login Reward
              </p>
              <p className="text-xs text-cyan-200/90">
                Claim every day — miss one and streak resets
              </p>
            </div>
          </div>
          {currentDay > 0 && (
            <div className="flex items-center gap-1 text-amber-300">
              <Flame className="w-4 h-4" />
              <span className="text-xs font-bold">
                {alreadyClaimedToday ? currentDay : streakBroken ? 0 : currentDay}d
              </span>
            </div>
          )}
        </div>

        {/* 10-day progression */}
        <div className="grid grid-cols-10 gap-1 mb-3">
          {REWARDS.map((amt, i) => {
            const day = i + 1;
            const claimed = !streakBroken && day <= currentDay && (alreadyClaimedToday || day < currentDay || (day === currentDay && lastClaimDate !== null));
            const isNext = !alreadyClaimedToday && day === nextDay;
            return (
              <div
                key={day}
                className={[
                  "rounded-md text-center py-1 px-0.5 text-[10px] font-bold border transition-all",
                  claimed
                    ? "bg-gradient-to-b from-emerald-500/40 to-emerald-700/30 border-emerald-400/60 text-emerald-100"
                    : isNext
                    ? "bg-gradient-to-b from-pink-500/40 to-purple-600/30 border-pink-400/70 text-white animate-pulse"
                    : "bg-white/5 border-white/10 text-purple-200/60",
                ].join(" ")}
              >
                <div className="leading-tight">D{day}</div>
                <div className="text-emerald-300 leading-tight">${amt}</div>
              </div>
            );
          })}
        </div>

        <Button
          onClick={handleClaim}
          disabled={loading || claiming || alreadyClaimedToday}
          className="w-full font-bold btn-game-neon border-0"
          size="sm"
        >
          {alreadyClaimedToday
            ? `Claimed today — back tomorrow`
            : claiming
            ? "Claiming..."
            : `Claim Day ${nextDay} — $${nextReward}`}
        </Button>
        {streakBroken && (
          <p className="text-[11px] text-amber-300/90 mt-2 text-center">
            Streak reset — you missed a day. Starting from Day 1.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
